import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core'
import { debounceTime, filter, takeUntil } from 'rxjs/operators'

import { ChatMessageDraftData, ChatModel, ChatUserModel } from '@shared/models/messenger/messenger.model'
import { DestroyService } from '@core/services/destroy.service'
import { User } from '@shared/models/user.model'
import { MessengerDraftService } from '../../services/messanger-draft.service'
import { AvatarSize } from '@shared/common-components/avatar/avatar.model'
import { sameId } from '@core/helpers/same-id'

@Component({
  selector: 'lin-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  standalone: false,
})
export class ChatComponent implements OnInit {
  AvatarSize = AvatarSize
  draftMessage: string

  @Input() chat: ChatModel
  @Input() isActive: boolean
  @Input() currentUser: User
  @Input() isToolbar: boolean

  get routerLink(): string[] {
    return [`./${this.chat.id}`]
  }

  get toolbarRouterLink(): string[] {
    return [`/client/messenger/${this.chat.id}`]
  }

  get isOnline(): boolean {
    const users = this.chat.users.filter(this.filterCurrent)
    return !!users?.[0]?.networkStatus
  }

  get isMyChat(): boolean {
    const isMe = (user: ChatUserModel): boolean => sameId(user.id, this.currentUser?.id)
    return !!this.chat?.users?.find(isMe)
  }

  get chatTitle(): string {
    const users = this.chat.users.filter(this.filterCurrent)
    return users.length ? users.map((user) => user.fullName).join(', ') : '-'
  }

  get avatarPersonal(): ChatUserModel {
    return this.chat.users.filter(this.filterCurrent)?.[0]
  }

  constructor(
    private readonly messengerDraftService: MessengerDraftService,
    private readonly destroyService: DestroyService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const draftMessageSubscribe = (messages: Record<number, ChatMessageDraftData>): void => {
      this.draftMessage = messages[this.chat.id]?.message
      this.cdr.markForCheck()
    }

    this.messengerDraftService.draftMessages$
      .pipe(
        filter(() => this.isActive),
        debounceTime(800),
        takeUntil(this.destroyService),
      )
      .subscribe(draftMessageSubscribe)
  }

  trackBy(_, { id }: ChatUserModel): string {
    return id
  }

  private filterCurrent = (user: ChatUserModel): boolean => !sameId(user.id, this.currentUser?.id)
}
